import React, { useCallback } from 'react'
import { useFieldArray, Controller, ArrayField } from 'react-hook-form'
import maxBy from 'lodash/maxBy'
import moment from 'moment'
import LocationPinAddIcon from 'transfix-icons/build/LocationPinAddIcon'
import { Button } from 'transfix-ui/components/Button'
import Box from 'transfix-ui/core/Box'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { StopCard } from 'apps/fleet-planner/components/create-load/StopCard'
import { STOP_TYPE, LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useCreateLoadFormContext } from 'apps/fleet-planner/context'
import { IShipmentStop } from 'apps/fleet-planner/schema'
import { getClosestTime } from 'apps/fleet-planner/utils/getClosestTime'
import { getDefaultStop } from 'apps/fleet-planner/utils/getDefaultStop'
import { getFieldsByStopType, getFieldStops } from 'apps/fleet-planner/utils/getFieldsByStopType'
import { FORMATS } from 'constants/datetime'

/**
 * Form section for Create Load form
 */
export const SectionStops = () => {
  const { control, register } = useCreateLoadFormContext()
  const { isMobile } = useBreakpointContext()

  const { append, fields: shipmentStopFields, remove } = useFieldArray<IShipmentStop, 'id'>({
    control,
    name: 'stops',
  })

  // Separated by `stopType`
  const { pickup: pickupStops = [], dropoff: dropoffStops = [] } = getFieldStops(shipmentStopFields)

  const getPreviousStop = useCallback(
    (stopType: STOP_TYPE) => {
      const allStops = getFieldsByStopType(shipmentStopFields, stopType)
      return maxBy(allStops, 'stopSequence')
    },
    [shipmentStopFields]
  )

  // Gets next sequence for each `stopType`
  const getNextSequence = useCallback(
    (stopType: STOP_TYPE): number => {
      const { stopSequence: prevStopSequence = 0 } = getPreviousStop(stopType) || {}
      return parseInt(`${prevStopSequence}`, 10) + 1
    },
    [getPreviousStop]
  )

  // Gets next startAt, which is an hour after
  const getNextStartAt = useCallback(
    (stopType: STOP_TYPE) => {
      const { startAt } = getPreviousStop(stopType) || {}

      if (startAt !== null && typeof startAt !== 'undefined') {
        return getClosestTime(moment(startAt)).format(FORMATS.fleetPlannerApiFormat)
      }

      return undefined
    },
    [getPreviousStop]
  )

  // Append a new stop to `shipmentStopFields`
  const addNewStop = useCallback(
    ({ stopType }: { stopType: STOP_TYPE }) => {
      const stopSequence = getNextSequence(stopType)
      const startAt = getNextStartAt(stopType)
      const newStop = getDefaultStop({
        defaultValues: {
          stopType,
          stopSequence,
          startAt,
        },
      })

      append(newStop)
    },
    [append, getNextSequence, getNextStartAt]
  )

  // Helper methods
  const addPickupStop = () => addNewStop({ stopType: STOP_TYPE.pickup })
  const addDropoffStop = () => addNewStop({ stopType: STOP_TYPE.dropoff })

  const getMapMarkerSequenceValue = (
    allStops: Partial<ArrayField<IShipmentStop, 'id'>>[] = [],
    stopSequence: number = 1
  ): number => {
    return allStops.length > 1 ? stopSequence : 0
  }

  /**
   * Generates a callback to remove the `removedStop` from array
   */
  const getRemoveStopHandler = (removedStop: Partial<ArrayField<IShipmentStop, 'id'>>) => () => {
    if (removedStop) {
      const removedStopIndex = shipmentStopFields.indexOf(removedStop)
      remove(removedStopIndex)
    }
  }

  return (
    <Box mb={3}>
      {/* Pickup Fields */}
      <Box mb={3}>
        {pickupStops.map((stopField: Partial<ArrayField<IShipmentStop, 'id'>>) => {
          const mapMarkervalue = getMapMarkerSequenceValue(pickupStops, stopField.stopSequence)
          const removeStopHandler = getRemoveStopHandler(stopField)
          const fieldIndex = shipmentStopFields.indexOf(stopField)
          return (
            <Box id={`section-pickup-${stopField.stopSequence}`} mb={3} key={stopField.id}>
              <StopCard
                stopField={stopField}
                mapMarkerValue={mapMarkervalue}
                fieldIndex={fieldIndex}
                register={register}
                onRemoveStop={removeStopHandler}
              />
            </Box>
          )
        })}
        <Button
          startIcon={<LocationPinAddIcon size='large' />}
          size={isMobile ? 'large' : 'medium'}
          onClick={addPickupStop}>
          {`Add Pickup ${getNextSequence(STOP_TYPE.pickup)}`}
        </Button>
      </Box>
      {/* Dropoff Fields */}
      <Box mb={3}>
        {dropoffStops.map(stopField => {
          const mapMarkervalue = getMapMarkerSequenceValue(dropoffStops, stopField.stopSequence)
          const removeStopHandler = getRemoveStopHandler(stopField)
          const fieldIndex = shipmentStopFields.indexOf(stopField)
          return (
            <Box id={`section-dropoff-${stopField.stopSequence}`} mb={3} key={stopField.id}>
              <StopCard
                register={register}
                stopField={stopField}
                mapMarkerValue={mapMarkervalue}
                onRemoveStop={removeStopHandler}
                fieldIndex={fieldIndex}
              />
            </Box>
          )
        })}
        <Button
          startIcon={<LocationPinAddIcon size='large' />}
          size={isMobile ? 'large' : 'medium'}
          onClick={addDropoffStop}>
          {`Add Dropoff ${getNextSequence(STOP_TYPE.dropoff)}`}
        </Button>
      </Box>
      {/**
       * Initializes fields in RHF
       */}
      <Controller
        name='customerNotes'
        defaultValue=''
        render={({ value }) => <input type='hidden' value={value} />}
      />
      <Controller
        name='status'
        defaultValue={LOAD_STATE.Pending}
        render={({ value }) => <input type='hidden' value={value} />}
      />
      <Controller
        name='dispatcher'
        defaultValue={{}}
        render={({ value }) => <input type='hidden' value={value} />}
      />
      <Controller
        name='drivers'
        defaultValue={[]}
        render={({ value }) => <input type='hidden' value={value} />}
      />
      <Controller
        name='isSample'
        defaultValue={false}
        render={({ value }) => <input type='hidden' value={value} />}
      />
      <Controller
        name='motorCarrier'
        defaultValue={{}}
        render={({ value }) => <input type='hidden' value={value} />}
      />
    </Box>
  )
}
